import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum';

/**
 * Raw NEXT_PUBLIC_DATADOG_* values. Built with literal `process.env.X` reads in
 * `env.ts` (Next.js inlines only literal references); this module stays pure so
 * the gating, validation and scrubbing are unit-testable.
 */
export interface RumEnv {
  applicationId: string | undefined;
  clientToken: string | undefined;
  site: string | undefined;
  privacyLevel: string | undefined;
  sessionSampleRate: string | undefined;
  replaySampleRate: string | undefined;
  env: string | undefined;
  version: string | undefined;
  /** Local-only: route intake through a capture server instead of Datadog (scripts/datadog-intake-capture.mjs). */
  proxy: string | undefined;
  /** How the 3D (WebGL) views are recorded in replays (`low` | `medium` | `high`); unset = not recorded. */
  canvasQuality: string | undefined;
}

export const RUM_SERVICE = 'photonium-insight-frontend';

const PRIVACY_LEVELS = ['mask', 'mask-user-input', 'allow'] as const;
type PrivacyLevel = (typeof PRIVACY_LEVELS)[number];

// The one query parameter the app puts a credential in: SSE endpoints take the
// bearer token as `?token=` because EventSource cannot send headers, and the
// magic-link callback carries its one-time token the same way. RUM resource and
// view events record full URLs, so it is stripped before anything leaves the tab.
const SCRUBBED_QUERY_PARAMS = ['token'] as const;

/** RUM is on when the three identifying values are all present. */
export function rumEnabled(rumEnv: RumEnv): boolean {
  return Boolean(rumEnv.applicationId && rumEnv.clientToken && rumEnv.site);
}

/**
 * The `datadogRum.init` options for this deployment, or null when RUM is off.
 * A partial or malformed configuration throws rather than silently sending
 * nothing (or sending everything unmasked): this runs from
 * `instrumentation-client.ts`, so the misconfiguration surfaces on the first
 * page load of the deploy that introduced it.
 */
export function rumInitConfiguration(rumEnv: RumEnv): RumInitConfiguration | null {
  const identity = {
    NEXT_PUBLIC_DATADOG_APPLICATION_ID: rumEnv.applicationId,
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: rumEnv.clientToken,
    NEXT_PUBLIC_DATADOG_SITE: rumEnv.site,
  };
  const missing = Object.entries(identity).filter(([, v]) => !v).map(([k]) => k);
  if (missing.length === 3) return null;
  if (missing.length > 0) {
    throw new Error(`Datadog RUM is partially configured; missing ${missing.join(', ')}`);
  }
  if (!rumEnv.env) {
    throw new Error('Datadog RUM needs NEXT_PUBLIC_DATADOG_ENV (or Vercel\'s NEXT_PUBLIC_VERCEL_ENV) to tag sessions with their environment');
  }

  return {
    applicationId: rumEnv.applicationId!,
    clientToken: rumEnv.clientToken!,
    site: rumEnv.site!,
    service: RUM_SERVICE,
    env: rumEnv.env,
    version: rumEnv.version,
    sessionSampleRate: samplePercentage('NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE', rumEnv.sessionSampleRate),
    sessionReplaySampleRate: samplePercentage('NEXT_PUBLIC_DATADOG_REPLAY_SAMPLE_RATE', rumEnv.replaySampleRate),
    defaultPrivacyLevel: privacyLevel(rumEnv.privacyLevel),
    // Session Replay already needs `worker-src blob:` (granted in next.config.ts);
    // compressing RUM batches through the same worker cuts intake bandwidth.
    compressIntakeRequests: true,
    beforeSend: scrubRumEvent,
    ...(rumEnv.proxy && { proxy: rumEnv.proxy }),
    ...canvasRecording(rumEnv.canvasQuality),
  };
}

// Replay records the DOM, so the 3D (WebGL) views replay blank unless the SDK
// also snapshots the canvas as images. The recorder is experimental and only
// works from 7.15 — see "3D views in replays" in docs/rum-session-replay.md
// for why the SDK is pinned exactly and what recording costs.
const CANVAS_EXPERIMENT = 'session_replay_record_canvas';
const CANVAS_QUALITIES = ['low', 'medium', 'high'] as const;

function canvasRecording(raw: string | undefined): Partial<RumInitConfiguration> {
  if (raw === undefined) return {};
  return {
    enableExperimentalFeatures: [CANVAS_EXPERIMENT],
    // The SDK would fill `quality: 'medium'` (4 fps, 1000 px) if it were left
    // out; passing it makes the deployment's choice the one that applies.
    sessionReplayCanvasRecording: {
      enable: true,
      quality: oneOf('NEXT_PUBLIC_DATADOG_CANVAS_QUALITY', CANVAS_QUALITIES, raw),
    },
  };
}

/** Narrows `raw` to one of `values`, or throws naming the variable; a misconfiguration surfaces on the first page load. */
function oneOf<const T extends readonly string[]>(name: string, values: T, raw: string): T[number] {
  if (!(values as readonly string[]).includes(raw)) {
    throw new Error(`${name} must be one of ${values.join(', ')}, got "${raw}"`);
  }
  return raw as T[number];
}

// Unset means "every session": the user base is small and the point of the
// rollout is to watch each one, so the knobs exist to dial cost DOWN later.
function samplePercentage(name: string, raw: string | undefined): number {
  if (raw === undefined) return 100;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0 || value > 100) {
    throw new Error(`${name} must be a number from 0 to 100, got "${raw}"`);
  }
  return value;
}

// `mask-user-input` records what the app shows (charts, tables, agent replies —
// the things a frustrated user is reacting to) while masking everything typed.
// The SDK additionally always masks password/email/tel inputs and elements with
// an `autocomplete` attribute, whatever level is set.
function privacyLevel(raw: string | undefined): PrivacyLevel {
  if (raw === undefined) return 'mask-user-input';
  return oneOf('NEXT_PUBLIC_DATADOG_PRIVACY_LEVEL', PRIVACY_LEVELS, raw);
}

/** Drops the credential-bearing query parameters; leaves anything unparsable untouched. */
export function scrubUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const dirty = SCRUBBED_QUERY_PARAMS.some((p) => parsed.searchParams.has(p));
  if (!dirty) return url;
  for (const p of SCRUBBED_QUERY_PARAMS) parsed.searchParams.delete(p);
  return parsed.toString();
}

/**
 * `beforeSend`: rewrites every URL an event carries. The event types declare
 * these fields readonly, but `view.url`, `view.referrer`, `resource.url` and
 * `error.resource.url` are the SDK's documented editable properties.
 */
export function scrubRumEvent(event: RumEvent): boolean {
  const view = event.view as { url: string; referrer?: string };
  view.url = scrubUrl(view.url);
  if (view.referrer) view.referrer = scrubUrl(view.referrer);
  if (event.type === 'resource') {
    const resource = event.resource as { url: string };
    resource.url = scrubUrl(resource.url);
  }
  if (event.type === 'error' && event.error.resource) {
    const resource = event.error.resource as { url: string };
    resource.url = scrubUrl(resource.url);
  }
  return true;
}
